import { LANGUAGE_CODES, type Language } from '@hero/schema';
import { COMPLETED_CHIP } from './order-status';
import { ordersCountLabel } from './present-order';
import { entityLink, type RenderProfile } from './render-profile';
import { agentCopy, type AgentCopyKey } from '../language/agent-copy';

/** Right-to-left mark — anchors a leading icon on the right in RTL chat. */
const RLM = '\u200F';

/** Title-only — order rows keep {@link COMPLETED_CHIP} so the header is not duplicated. */
const ORDER_CREATED_TITLE_CHIP = '🚀';

/**
 * One localized "this is done" line for every confirmed mutation.
 *
 * Before this existed each confirmation handler hand-wrote its own
 * result text, which is why several of them shipped English strings
 * into Arabic threads and only some carried a completion mark. Route
 * every successful mutation through here so the copy, the language,
 * and the chip stay in one place (ADR-019).
 */
export type ActionResultKind =
  | 'order_created'
  | 'order_updated'
  | 'order_escalated'
  | 'client_created'
  | 'contact_created'
  | 'contact_updated'
  | 'contact_deleted'
  | 'saved_location_created'
  | 'saved_location_updated'
  | 'driver_assigned'
  | 'bulk_assigned'
  | 'financing_submitted'
  | 'financing_accepted'
  | 'offer_accepted'
  | 'offer_declined'
  | 'counter_offer_sent'
  | 'lead_saved'
  | 'support_ticket_opened'
  | 'government_waybill_issued'
  | 'government_waybill_issued_direct'
  | 'government_waybill_batch_issued'
  | 'government_waybill_closed'
  | 'government_waybill_canceled'
  | 'government_waybill_reissued'
  | 'account_created';

interface TemplateContext {
  /** Primary entity label, already link-wrapped for the surface. */
  subject: string;
  /** Secondary entity label (e.g. the order a driver was assigned to). */
  target: string;
  /** Localized count label for bulk kinds. */
  countLabel: string;
  /** Free-form trailing detail (status value, price, action name). */
  detail: string;
}

type Template = (ctx: TemplateContext) => string;

const join = (...parts: Array<string | undefined>): string =>
  parts.filter(part => part && part.length > 0).join(' ');

/** A result line that takes no subject, count, or detail. */
const fixed = (key: AgentCopyKey): Record<Language, Template> => {
  const templates = {} as Record<Language, Template>;
  for (const code of LANGUAGE_CODES) templates[code] = () => agentCopy(code, key);
  return templates;
};

const TEMPLATES: Record<ActionResultKind, Record<Language, Template>> = {
  order_created: {
    en: c => join('Created', c.countLabel, c.subject && `for ${c.subject}`),
    ar: c => join('تم إنشاء', c.countLabel, c.subject && `لـ ${c.subject}`),
    ur: c => join(c.subject && `${c.subject} کے لیے`, c.countLabel, 'بن گئے'),
  },
  order_updated: {
    en: c => join('Order', c.subject, 'updated', c.detail && `— ${c.detail}`),
    ar: c => join('تم تحديث الطلب', c.subject, c.detail && `— ${c.detail}`),
    ur: c => join('آرڈر', c.subject, 'اپڈیٹ ہو گیا', c.detail && `— ${c.detail}`),
  },
  order_escalated: {
    en: c => join('Order', c.subject, 'escalated'),
    ar: c => join('تم تصعيد الطلب', c.subject),
    ur: c => join('آرڈر', c.subject, 'آگے بڑھا دیا گیا'),
  },
  client_created: {
    en: c => join('Client', c.subject, 'created'),
    ar: c => join('تم إنشاء العميل', c.subject),
    ur: c => join('کلائنٹ', c.subject, 'بن گیا'),
  },
  contact_created: {
    en: c => join('Contact', c.subject, 'created'),
    ar: c => join('تم إنشاء جهة الاتصال', c.subject),
    ur: c => join('رابطہ', c.subject, 'بن گیا'),
  },
  contact_updated: {
    en: c => join('Contact', c.subject, 'updated'),
    ar: c => join('تم تحديث جهة الاتصال', c.subject),
    ur: c => join('رابطہ', c.subject, 'اپڈیٹ ہو گیا'),
  },
  contact_deleted: {
    en: c => join('Contact', c.subject, 'deleted'),
    ar: c => join('تم حذف جهة الاتصال', c.subject),
    ur: c => join('رابطہ', c.subject, 'حذف ہو گیا'),
  },
  saved_location_created: {
    en: c => join('Location', c.subject, 'saved'),
    ar: c => join('تم حفظ الموقع', c.subject),
    ur: c => join('مقام', c.subject, 'محفوظ ہو گیا'),
  },
  saved_location_updated: {
    en: c => join('Saved location', c.subject, 'updated'),
    ar: c => join('تم تحديث الموقع المحفوظ', c.subject),
    ur: c => join('محفوظ مقام', c.subject, 'اپڈیٹ ہو گیا'),
  },
  driver_assigned: {
    en: c => join('Assigned', c.subject, c.target && `to ${c.target}`),
    ar: c => join('تم تعيين', c.subject, c.target && `على الطلب ${c.target}`),
    ur: c => join(c.subject, c.target && `کو ${c.target} پر`, 'مقرر کر دیا'),
  },
  bulk_assigned: {
    en: c => join('Assigned', c.countLabel),
    ar: c => join('تم تعيين', c.countLabel),
    ur: c => join(c.countLabel, 'مقرر ہو گئے'),
  },
  financing_submitted: {
    en: c => join('Financing application', c.subject, 'submitted', c.detail && `(${c.detail})`),
    ar: c => join('تم إرسال طلب التمويل', c.subject, c.detail && `(${c.detail})`),
    ur: c => join('فنانسنگ کی درخواست', c.subject, 'جمع ہو گئی', c.detail && `(${c.detail})`),
  },
  financing_accepted: fixed('orders.action_result.financing_accepted'),
  offer_accepted: {
    en: c => join("Offer accepted — you're on order", c.target),
    ar: c => join('تم قبول العرض — أنت الآن على الطلب', c.target),
    ur: c => join('پیشکش قبول ہو گئی — آپ آرڈر', c.target, 'پر ہیں'),
  },
  offer_declined: fixed('orders.action_result.offer_declined'),
  counter_offer_sent: {
    en: c => join('Counter offer sent', c.detail && `at ${c.detail}`),
    ar: c => join('تم إرسال السعر المضاد', c.detail && `بقيمة ${c.detail}`),
    ur: c => join('جوابی قیمت بھیج دی', c.detail && `${c.detail} پر`),
  },
  lead_saved: fixed('orders.action_result.lead_saved'),
  support_ticket_opened: fixed('orders.action_result.support_ticket_opened'),
  government_waybill_issued: {
    en: c => join('Waybill issued for order', c.subject, c.detail && `— ${c.detail}`),
    ar: c => join('تم إصدار وثيقة النقل للطلب', c.subject, c.detail && `— ${c.detail}`),
    ur: c => join('آرڈر', c.subject, 'کی وثیقہ جاری ہو گئی', c.detail && `— ${c.detail}`),
  },
  // Waybill-first: the subject is the document number, not an order ref.
  // The order exists, but the user never asked for one and cannot look
  // it up — leading with a reference they don't recognise makes them
  // check whether something else got created by mistake.
  government_waybill_issued_direct: {
    en: c => join('Waybill issued', c.subject, c.detail && `— ${c.detail}`),
    ar: c => join('تم إصدار وثيقة النقل', c.subject, c.detail && `— ${c.detail}`),
    ur: c => join('وثيقة النقل جاری ہو گئی', c.subject, c.detail && `— ${c.detail}`),
  },
  // Several documents from one confirmation. `subject` is an already
  // pluralized count label ("4 وثائق بيان") rather than a bare number,
  // because Arabic needs three different forms of the noun to say it and
  // the caller is the only place that knows the count.
  government_waybill_batch_issued: {
    en: c => join('Issued', c.subject),
    ar: c => join('تم إصدار', c.subject),
    ur: c => join(c.subject, 'جاری ہو گئیں'),
  },
  // Closed = the trip happened and the document is signed off. Voided =
  // it did not, and the document is struck. Both end the cycle with
  // Bayan, and the two words are not interchangeable to anyone who gets
  // inspected, so the copy never blurs them into "done".
  government_waybill_closed: {
    en: c => join('Waybill', c.subject, 'closed'),
    ar: c => join('تم إغلاق وثيقة النقل', c.subject),
    ur: c => join('وثیقہ', c.subject, 'بند ہو گئی'),
  },
  government_waybill_canceled: {
    en: c => join('Waybill', c.subject, 'cancelled'),
    ar: c => join('تم إلغاء وثيقة النقل', c.subject),
    ur: c => join('وثیقہ', c.subject, 'منسوخ ہو گئی'),
  },
  government_waybill_reissued: {
    en: c => join('New waybill issued', c.subject, c.detail && `— ${c.detail} cancelled`),
    ar: c => join('تم إصدار وثيقة جديدة', c.subject, c.detail && `— وأُلغيت ${c.detail}`),
    ur: c => join('نئی وثیقہ جاری ہو گئی', c.subject, c.detail && `— ${c.detail} منسوخ`),
  },
  // Deliberately brand-free: the same line is read on the white-label
  // line, where naming the platform would be a leak.
  account_created: {
    en: c => join('Account created for', c.subject),
    ar: c => join('تم إنشاء حساب', c.subject),
    ur: c => join(c.subject, 'کا اکاؤنٹ بن گیا'),
  },
};

export interface FormatActionResultInput {
  kind: ActionResultKind;
  language: Language;
  profile: RenderProfile;
  /** Primary entity name / ref (client, contact, driver, application ref). */
  subject?: string;
  /** Deep-link target for `subject` on link-capable surfaces. */
  subjectHref?: string;
  /** Secondary entity label (the order in an assign / offer result). */
  target?: string;
  targetHref?: string;
  /** Bulk count — rendered via the localized orders-count label. */
  count?: number;
  /** Trailing detail: new status, price, action name. */
  detail?: string;
  /** Suppress the completion chip (rare — prefer leaving it on). */
  omitChip?: boolean;
}

/** Mirror confirmation headers (`📦 *…*`) on order-create titles. */
const emphasizeOrderCreatedTitle = (text: string, profile: RenderProfile): string => {
  if (profile.emphasis !== 'whatsapp') return text;
  const starIdx = text.indexOf('*');
  if (starIdx === -1) return `*${text}*`;
  const prefix = text.slice(0, starIdx).trimEnd();
  const rest = text.slice(starIdx);
  return prefix ? `*${prefix}* ${rest}` : text;
};

const withLeadingChip = (text: string, language: Language, chip: string): string => {
  const line = `${chip} ${text}`;
  return language === 'ar' || language === 'ur' ? `${RLM}${line}` : line;
};

/**
 * Render a completed mutation as one localized line with a leading
 * completion chip (same placement as 📦 on the confirm card).
 *
 * `subject` / `target` are wrapped per the render profile: a tappable
 * `hero://` link in-app, bold text on WhatsApp, plain on voice.
 */
export const formatActionResult = (input: FormatActionResultInput): string => {
  const { language, profile } = input;
  const templates = TEMPLATES[input.kind];
  const render = templates[language] ?? templates.en;

  let text = render({
    subject: entityLink({ label: input.subject, href: input.subjectHref }, profile),
    target: entityLink({ label: input.target, href: input.targetHref }, profile),
    countLabel:
      typeof input.count === 'number' ? ordersCountLabel(input.count, language) : '',
    detail: input.detail?.trim() ?? '',
  }).trim();

  if (input.kind === 'order_created') {
    text = emphasizeOrderCreatedTitle(text, profile);
  }

  const chip = input.kind === 'order_created' ? ORDER_CREATED_TITLE_CHIP : COMPLETED_CHIP;
  return input.omitChip ? text : withLeadingChip(text, language, chip);
};
